import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, CheckCircle, XCircle, ArrowRight } from "lucide-react";
import { recruitmentAPI } from "../services/api";

const STAGES = [
  { id: "new",       color: "#5e6ad2", bg: "rgba(94,106,210,0.10)" },
  { id: "screening", color: "#d97706", bg: "rgba(217,119,6,0.10)" },
  { id: "interview", color: "#7c3aed", bg: "rgba(124,58,237,0.10)" },
  { id: "offer",     color: "#0284c7", bg: "rgba(2,132,199,0.10)" },
  { id: "hired",     color: "#16a34a", bg: "rgba(22,163,74,0.10)" },
  { id: "rejected",  color: "#dc2626", bg: "rgba(220,38,38,0.10)" },
];

export default function PipelineKanban() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedPosting, setSelectedPosting] = useState(null);

  const { data: postings = [] } = useQuery({
    queryKey: ["job-postings", "published"],
    queryFn: () => recruitmentAPI.listPostings({ status: "published" }).then((r) => r.data),
  });

  const { data: applications = [], isLoading } = useQuery({
    queryKey: ["applications", selectedPosting],
    queryFn: () =>
      selectedPosting
        ? recruitmentAPI.listApplicationsForPosting(selectedPosting).then((r) => r.data)
        : recruitmentAPI.listApplications({}).then((r) => r.data),
    enabled: true,
  });

  const moveMut = useMutation({
    mutationFn: ({ appId, stage }) => recruitmentAPI.moveStage(appId, stage),
    onSuccess: () => qc.invalidateQueries(["applications"]),
  });

  const rejectMut = useMutation({
    mutationFn: ({ appId }) => recruitmentAPI.rejectApplication(appId, null),
    onSuccess: () => qc.invalidateQueries(["applications"]),
  });

  const hireMut = useMutation({
    mutationFn: (appId) => recruitmentAPI.hireApplication(appId),
    onSuccess: () => qc.invalidateQueries(["applications"]),
  });

  // Group applications by stage
  const byStage = STAGES.reduce((acc, s) => {
    acc[s.id] = applications.filter((a) => a.stage === s.id);
    return acc;
  }, {});

  // Drag state
  const [dragging, setDragging] = useState(null);

  const handleDragStart = (e, app) => {
    setDragging(app);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    if (dragging && dragging.stage !== targetStage) {
      moveMut.mutate({ appId: dragging.id, stage: targetStage });
    }
    setDragging(null);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  return (
    <div className="space-y-4">
      {/* Posting selector */}
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("recruitment.pipeline.posting")}</span>
        <select
          value={selectedPosting || ""}
          onChange={(e) => setSelectedPosting(e.target.value || null)}
          className="px-3 py-1.5 rounded-lg text-sm"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
        >
          <option value="">{t("recruitment.pipeline.allPostings")}</option>
          {postings.map((p) => (
            <option key={p.id} value={p.id}>{p.title}</option>
          ))}
        </select>
        <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
          {t("recruitment.pipeline.applications", { count: applications.length })}
        </span>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm" style={{ color: "var(--lm-ink-3)" }}>{t("common.loading")}</div>
      ) : (
        /* Kanban board — horizontal scroll on mobile */
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-3 min-w-max">
            {STAGES.map((stage) => {
              const cards = byStage[stage.id] || [];
              return (
                <div
                  key={stage.id}
                  className="flex flex-col rounded-xl w-56 flex-shrink-0"
                  style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)", minHeight: "320px" }}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  {/* Column header */}
                  <div
                    className="flex items-center justify-between px-3 py-2.5 rounded-t-xl"
                    style={{ borderBottom: "1px solid var(--lm-border)" }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: stage.color }}
                      />
                      <span className="text-xs font-semibold" style={{ color: "var(--lm-ink)" }}>
                        {t(`recruitment.stages.${stage.id}`)}
                      </span>
                    </div>
                    <span
                      className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                      style={{ color: stage.color, backgroundColor: stage.bg }}
                    >
                      {cards.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="flex flex-col gap-2 p-2 flex-1">
                    {cards.map((app) => (
                      <ApplicationCard
                        key={app.id}
                        app={app}
                        stages={STAGES}
                        onDragStart={handleDragStart}
                        onMove={(targetStage) => moveMut.mutate({ appId: app.id, stage: targetStage })}
                        onHire={() => hireMut.mutate(app.id)}
                        onReject={() => rejectMut.mutate({ appId: app.id })}
                      />
                    ))}
                    {cards.length === 0 && (
                      <div
                        className="flex-1 flex items-center justify-center text-xs rounded-lg border-2 border-dashed"
                        style={{ color: "var(--lm-ink-3)", borderColor: "var(--lm-border)", minHeight: "80px" }}
                      >
                        {t("recruitment.pipeline.dropHere")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ApplicationCard({ app, stages, onDragStart, onMove, onHire, onReject }) {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const candidate = app.candidate;
  const name = candidate ? `${candidate.first_name} ${candidate.last_name}` : t("recruitment.pipeline.candidateFallback", { id: app.candidate_id });
  const nextStages = stages.filter((s) => s.id !== app.stage && s.id !== "rejected" && s.id !== "hired");

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, app)}
      className="rounded-lg px-3 py-3 cursor-grab active:cursor-grabbing select-none"
      style={{
        backgroundColor: "var(--lm-s1)",
        border: "1px solid var(--lm-border)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold"
            style={{ backgroundColor: "rgba(94,106,210,0.15)", color: "#5e6ad2" }}
          >
            {name[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium truncate" style={{ color: "var(--lm-ink)" }}>{name}</p>
            {candidate?.email && (
              <p className="text-xs truncate" style={{ color: "var(--lm-ink-3)" }}>{candidate.email}</p>
            )}
          </div>
        </div>
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="p-1 rounded"
            style={{ color: "var(--lm-ink-3)" }}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-6 z-10 rounded-lg py-1 w-36"
              style={{ backgroundColor: "var(--lm-s3)", border: "1px solid var(--lm-border)" }}
              onMouseLeave={() => setMenuOpen(false)}
            >
              {nextStages.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { onMove(s.id); setMenuOpen(false); }}
                  className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-all"
                  style={{ color: s.color }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--lm-s2)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                >
                  <ArrowRight className="h-3 w-3" />
                  {t(`recruitment.stages.${s.id}`)}
                </button>
              ))}
              <div style={{ height: "1px", backgroundColor: "var(--lm-border)", margin: "4px 0" }} />
              <button
                onClick={() => { onHire(); setMenuOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2"
                style={{ color: "#16a34a" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--lm-s2)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                <CheckCircle className="h-3 w-3" />
                {t("recruitment.pipeline.hire")}
              </button>
              <button
                onClick={() => { onReject(); setMenuOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2"
                style={{ color: "#dc2626" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--lm-s2)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                <XCircle className="h-3 w-3" />
                {t("recruitment.pipeline.reject")}
              </button>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs mt-2" style={{ color: "var(--lm-ink-3)" }}>
        {new Date(app.applied_at).toLocaleDateString(i18n.language, { day: "numeric", month: "short" })}
      </p>
    </div>
  );
}
